import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  useResizeColumns,
  usePagination,
} from "react-table";
import { FiEdit, FiTrash } from "react-icons/fi";
import UserHeader from "./UserHeader";
import Footer from "../Home/Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import AxiosInstance from "../Home/AxiosInstance";
import "./UserProfile.css";
import "./employerPortal.css";

export default function UserProfile() {
  const [userId, setUserId] = useState(null);
  const [internships, setInternships] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filterInput, setFilterInput] = useState("");
  const [jobFilterInput, setJobFilterInput] = useState("");
  const [editingInternship, setEditingInternship] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [editingJob, setEditingJob] = useState(null);
  const [editedJobData, setEditedJobData] = useState({});
  const [viewingContent, setViewingContent] = useState(null);
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [CourseMaterialFilter, setCourseMaterialFilter] = useState("");
  const [editingCourseMaterial, setEditingCourseMaterial] = useState(null);
  const [editedCourseMaterial, setEditedCourseMaterial] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [educationOptions, setEducationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [userRole, setUserRole] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    AxiosInstance.get("companies/educations/")
      .then((res) => {
        setEducationOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setEducationOptions([]));

    AxiosInstance.get("companies/departments/")
      .then((res) => {
        setDepartmentOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setDepartmentOptions([]));
  }, []);

  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("userId");
    if (stored) setUserId(parseInt(stored));
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("User ID is missing. Please log in.");
      return;
    }
    if (userRole === "HR" && userId) {
      const fetchInternships = async () => {
        try {
          const res = await AxiosInstance.get(
            `companies/internships/${userId}/`
          );
          setInternships(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
          console.error("Error fetching internships:", err);
          setInternships([]);
        }
      };
      const fetchJobs = async () => {
        try {
          const res = await AxiosInstance.get(`companies/jobs/${userId}/`);
          setJobs(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
          console.error("Error fetching jobs:", err);
          setJobs([]);
        }
      };
      fetchInternships();
      fetchJobs();
    }
  }, [userRole, userId]);

const handleSave = async () => {
  try {
    const payload = {
      ...editedData,
      company: editedData.company?.company_id || editedData.company,
    };

    await AxiosInstance.put(
      `companies/internships/${editedData.internship_id}`,
      payload
    );

    // Update state locally
    setInternships((prev) =>
      prev.map((internship) =>
        internship.internship_id === editedData.internship_id
          ? { ...internship, ...editedData }
          : internship
      )
    );

    setEditingInternship(null);
  } catch (err) {
    console.error("Error saving internship:", err);
  }
};

const handleDelete = async (id) => {
  if (!window.confirm("Delete this internship?")) return;
  try {
    await AxiosInstance.delete(`companies/company/internship/${id}/`);

    // Remove deleted internship from state immediately
    setInternships((prev) =>
      prev.filter((internship) => internship.internship_id !== id)
    );

    alert("Internship deleted successfully!");
  } catch (err) {
    console.error("Error deleting internship:", err);
    alert("Failed to delete internship. Please try again.");
  }
};

const handleSaveJob = async () => {
  try {
    const payload = {
      ...editedJobData,
      company: editedJobData.company?.company_id || editedJobData.company,
    };

    await AxiosInstance.put(
      `companies/jobs/${editedJobData.job_id}`,
      payload
    );

    setJobs((prev) =>
      prev.map((job) =>
        job.job_id === editedJobData.job_id
          ? { ...job, ...editedJobData }
          : job
      )
    );

    setEditingJob(null);
  } catch (err) {
    console.error("Error saving job:", err);
  }
};

const handleDeleteJob = async (id) => {
  if (!window.confirm("Delete this job?")) return;
  try {
    await AxiosInstance.delete(`companies/company/job/${id}/`);
    setJobs((prev) => prev.filter((job) => job.job_id !== id));
    alert("Job deleted successfully!");
  } catch (err) {
    console.error("Error deleting job:", err);
    alert("Failed to delete job. Please try again.");
  }
};


  const columns = useMemo(
    () => [
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ row }) => (
          <button
            className="btn btn-link p-0"
            style={{
              textDecoration: "underline",
              cursor: "pointer",
              width: "200px",
            }}
            onClick={() =>
              navigate(`/user-internship/${row.original.internship_id}`)
            }
          >
            {row.original.role}
          </button>
        ),
      },
      {
        Header: "Job Description",
        accessor: "job_description",
        resizable: true,
        Cell: ({ value, row }) => {
          if (!value) return null;
          return (
            <div className="job-content-cell">
              <div className="job-content-preview">{value}</div>
              <button
                type="button"
                className="job-content-view-more"
                onClick={() =>
                  setViewingContent({
                    title: row.original.role,
                    content: value,
                  })
                }
              >
                view more
              </button>
            </div>
          );
        },
      },
      {
        Header: "Skills",
        accessor: "skills",
        resizable: true,
        Cell: ({ value }) => (
          <div
            style={{
              width: "200px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {value}
          </div>
        ),
      },
      { Header: "Location", accessor: "location", resizable: true },
      { Header: "Department", accessor: "department", resizable: true },
      { Header: "Education", accessor: "education", resizable: true },
      { Header: "Work Mode", accessor: "work_mode", resizable: true },
      { Header: "Type", accessor: "internship_type", resizable: true },
      { Header: "Stipend", accessor: "stipend", resizable: true },
      { Header: "Duration", accessor: "duration", resizable: true },
      { Header: "Vacancies", accessor: "vacancies", resizable: true },
      { Header: "Start Date", accessor: "start_date", resizable: true },
      { Header: "End Date", accessor: "end_date", resizable: true },
      { Header: "Apply By", accessor: "apply_by", resizable: true },
      { Header: "Status", accessor: "status", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setEditingInternship(row.original);
                setEditedData({ ...row.original });
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row.original.internship_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const jobColumns = useMemo(
    () => [
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ row }) => (
          <button
            className="btn btn-link p-0"
            style={{
              textDecoration: "underline",
              cursor: "pointer",
              width: "200px",
            }}
            onClick={() => navigate(`/user-job/${row.original.job_id}`)}
          >
            {row.original.role}
          </button>
        ),
      },
      {
        Header: "Job Description",
        accessor: "job_description",
        resizable: true,
        Cell: ({ value, row }) => {
          if (!value) return null;
          return (
            <div className="job-content-cell">
              <div className="job-content-preview">{value}</div>
              <button
                type="button"
                className="job-content-view-more"
                onClick={() =>
                  setViewingContent({
                    title: row.original.role,
                    content: value,
                  })
                }
              >
                view more
              </button>
            </div>
          );
        },
      },
      {
        Header: "Skills",
        accessor: "skills",
        resizable: true,
        Cell: ({ value }) => (
          <div
            style={{
              width: "200px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {value}
          </div>
        ),
      },
      { Header: "Location", accessor: "location", resizable: true },
      { Header: "Department", accessor: "department", resizable: true },
      { Header: "Education", accessor: "education", resizable: true },
      { Header: "Work Mode", accessor: "work_mode", resizable: true },
      { Header: "Type", accessor: "employment_type", resizable: true },
      { Header: "Salary", accessor: "salary", resizable: true },
      { Header: "Experience", accessor: "experience", resizable: true },
      { Header: "Vacancies", accessor: "vacancies", resizable: true },
      { Header: "Start Date", accessor: "start_date", resizable: true },
      { Header: "End Date", accessor: "end_date", resizable: true },
      { Header: "Apply By", accessor: "apply_by", resizable: true },
      { Header: "Status", accessor: "status", resizable: true },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setEditingJob(row.original);
                setEditedJobData({ ...row.original });
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeleteJob(row.original.job_id)}
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const data = useMemo(() => internships, [internships]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex },
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setGlobalFilter,
  } = useTable(
    { columns, data },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter(filterInput || undefined);
  }, [filterInput, setGlobalFilter]);

  const jobData = useMemo(() => jobs, [jobs]);

  const {
    getTableProps: getJobTableProps,
    getTableBodyProps: getJobTableBodyProps,
    headerGroups: jobHeaderGroups,
    page: jobPage,
    prepareRow: prepareJobRow,
    state: { pageIndex: jobPageIndex },
    nextPage: nextJobPage,
    previousPage: previousJobPage,
    canNextPage: canNextJobPage,
    canPreviousPage: canPreviousJobPage,
    pageOptions: jobPageOptions,
    setGlobalFilter: setJobGlobalFilter,
  } = useTable(
    { columns: jobColumns, data: jobData },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setJobGlobalFilter(jobFilterInput || undefined);
  }, [jobFilterInput, setJobGlobalFilter]);

  useEffect(() => {
    if (userId) {
      fetchCourseMaterials();
      fetchDropdownData();
    }
  }, [userId]);

  const fetchCourseMaterials = async () => {
    try {
      const res = await AxiosInstance.get(
        `companies/user/courses/${userId}/`
      );
      setCourseMaterials(res.data);
    } catch (err) {
      console.error("Error fetching course materials:", err);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [profilesRes, coursesRes] = await Promise.all([
        AxiosInstance.get(`companies/user/profile/${userId}/`),
        AxiosInstance.get("companies/courses/"),
      ]);
      setProfiles(profilesRes.data);
      setCourseOptions(coursesRes.data);
    } catch (err) {
      console.error("Error fetching dropdown data:", err);
    }
  };

  const handleSaveCourseMaterial = async () => {
    try {
      const payload = {
        ...editedCourseMaterial,
        user: userId,
        course: editedCourseMaterial.course,
        profile: editedCourseMaterial.profile,
      };
      await AxiosInstance.put(
        `companies/courses/${editedCourseMaterial.course_material_id}/`,
        payload
      );
      setEditingCourseMaterial(null);
      fetchCourseMaterials();
    } catch (err) {
      console.error("Error saving course material:", err);
    }
  };

  const handleDeleteCourseMaterial = async (courseMaterialId) => {
    if (
      !window.confirm("Are you sure you want to delete this course material?")
    )
      return;
    try {
      await AxiosInstance.delete(
        `companies/course/${courseMaterialId}/`
      );
      setCourseMaterials((prev) =>
        prev.filter((m) => m.course_material_id !== courseMaterialId)
      );
    } catch (err) {
      console.error("Error deleting course material:", err);
    }
  };

  const courseColumns = useMemo(
    () => [
      {
        Header: "Course Name",
        accessor: "course.course_name",
        id: "course_name",
        Cell: ({ row }) => {
          const courseName = row.original.course?.course_name || "N/A";
          const courseMaterialId = row.original.course_material_id;
          const handleClick = () => {
            localStorage.setItem("selectedCourseMaterialId", courseMaterialId);
            navigate(`/course-material/${courseMaterialId}`);
          };
          return (
            <span
              onClick={handleClick}
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {courseName}
            </span>
          );
        },
      },
      {
        Header: "Profile",
        accessor: (row) => row.profile?.profile_name || "N/A",
        id: "profile_name",
      },
      { Header: "Sub Topics", accessor: "sub_topics" },
      { Header: "Description", accessor: "description" },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                const original = row.original;
                setEditingCourseMaterial(original);
                setEditedCourseMaterial({
                  ...original,
                  course: original.course?.course_id || "",
                  profile: original.profile?.profile_id || "",
                });
              }}
            >
              <FiEdit />
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() =>
                handleDeleteCourseMaterial(row.original.course_material_id)
              }
            >
              <FiTrash />
            </button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const courseData = useMemo(() => courseMaterials, [courseMaterials]);

  const {
    getTableProps: getCourseTableProps,
    getTableBodyProps: getCourseTableBodyProps,
    headerGroups: courseHeaderGroups,
    page: coursePage,
    prepareRow: prepareCourseRow,
    state: { pageIndex: coursePageIndex },
    nextPage: nextCoursePage,
    previousPage: previousCoursePage,
    canNextPage: canNextCoursePage,
    canPreviousPage: canPreviousCoursePage,
    pageOptions: coursePageOptions,
    setGlobalFilter: setCourseGlobalFilter,
  } = useTable(
    {
      columns: courseColumns,
      data: courseData,
    },
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    usePagination
  );

  useEffect(() => {
    setCourseGlobalFilter(CourseMaterialFilter);
  }, [CourseMaterialFilter, setGlobalFilter]);

  return (
    <div className="CompanyPage als-employer-page">
      <UserHeader />
      <div className="company-container my-4">
        {userRole === "HR" ? (
          <>
            {/* Internship Experience Section */}
            <h2 className="section-heading">Internships:</h2>
            <div className="card-custom">
              <div className="mb-3 d-flex justify-content-between">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search internships..."
                  value={filterInput}
                  onChange={(e) => setFilterInput(e.target.value)}
                />
                <button
                  className="add-button"
                  onClick={() => navigate("/add-internship")}
                >
                  Add
                </button>
              </div>

              <div className="table-responsive">
                <table
                  {...getTableProps()}
                  className="table table-bordered table-striped"
                >
                  <thead className="table-light">
                    {headerGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            key={col.id}
                          >
                            {col.render("Header")}
                            {col.isSorted
                              ? col.isSortedDesc
                                ? " ▼"
                                : " ▲"
                              : ""}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} key={row.id}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} key={cell.column.id}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {page.length === 0 && (
                      <tr>
                        <td colSpan={columns.length}>No internships found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="pagination d-flex align-items-center gap-2 mt-3">
                  <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    className="btn btn-sm btn-outline-primary"
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{pageIndex + 1}</strong> of{" "}
                    <strong>{pageOptions.length}</strong>
                  </span>
                  <button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    className="btn btn-sm btn-outline-primary"
                  >
                    Next
                  </button>
                </div>
              </div>

              {editingInternship && (
                <div
                  className="modal d-block"
                  tabIndex="-1"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Edit Internship</h5>
                      </div>
                      <div className="modal-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Role</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedData.role || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  role: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Location</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedData.location || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  location: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Department</label>
                            <select
                              className="form-select"
                              value={editedData.department || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  department: e.target.value,
                                })
                              }
                            >
                              <option value="">Select department</option>
                              {departmentOptions.map((dept) => (
                                <option
                                  key={dept.department_id}
                                  value={dept.department_name}
                                >
                                  {dept.department_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Education</label>
                            <select
                              className="form-select"
                              value={editedData.education || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  education: e.target.value,
                                })
                              }
                            >
                              <option value="">Select education</option>
                              {educationOptions.map((edu) => (
                                <option
                                  key={edu.education_id}
                                  value={edu.qualification}
                                >
                                  {edu.qualification}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Stipend</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedData.stipend || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  stipend: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Duration</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedData.duration || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  duration: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Skills</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedData.skills || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  skills: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Status</label>
                            <select
                              className="form-select"
                              value={editedData.status || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  status: e.target.value,
                                })
                              }
                            >
                              <option value="">Select Status</option>
                              <option value="Open">Open</option>
                              <option value="Closed">Closed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Apply Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={editedData.apply_by || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  apply_by: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Start Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={editedData.start_date || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  start_date: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">End Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={editedData.end_date || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  end_date: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Work Mode</label>
                            <select
                              className="form-select"
                              value={editedData.work_mode || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  work_mode: e.target.value,
                                })
                              }
                            >
                              <option value="">Select work moode</option>
                              <option value="Remote">Remote</option>
                              <option value="Hybrid">Hybrid</option>
                              <option value="Onsite">Onsite</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Internship Type
                            </label>
                            <select
                              className="form-select"
                              value={editedData.internship_type || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  internship_type: e.target.value,
                                })
                              }
                            >
                              <option value="" disabled>
                                Select Type
                              </option>
                              <option value="Full Time">Full Time</option>
                              <option value="Part Time">Part Time</option>
                            </select>
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="form-label">
                              Job Description
                            </label>
                            <textarea
                              rows={3}
                              className="form-control"
                              value={editedData.job_description || ""}
                              onChange={(e) =>
                                setEditedData({
                                  ...editedData,
                                  job_description: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn btn-secondary"
                          onClick={() => setEditingInternship(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleSave}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <h2 className="section-heading">Jobs:</h2>
            <div className="card-custom">
              <div className="mb-3 d-flex justify-content-between">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search jobs..."
                  value={jobFilterInput}
                  onChange={(e) => setJobFilterInput(e.target.value)}
                />
                <button
                  className="add-button"
                  onClick={() => navigate("/add-job")}
                >
                  Add
                </button>
              </div>

              <div className="table-responsive">
                <table
                  {...getJobTableProps()}
                  className="table table-bordered table-striped"
                >
                  <thead className="table-light">
                    {jobHeaderGroups.map((hg) => (
                      <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                        {hg.headers.map((col) => (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            key={col.id}
                          >
                            {col.render("Header")}
                            {col.isSorted
                              ? col.isSortedDesc
                                ? " ▼"
                                : " ▲"
                              : ""}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getJobTableBodyProps()}>
                    {jobPage.map((row) => {
                      prepareJobRow(row);
                      return (
                        <tr {...row.getRowProps()} key={row.id}>
                          {row.cells.map((cell) => (
                            <td {...cell.getCellProps()} key={cell.column.id}>
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {jobPage.length === 0 && (
                      <tr>
                        <td colSpan={jobColumns.length}>No jobs found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="pagination d-flex align-items-center gap-2 mt-3">
                  <button
                    onClick={() => previousJobPage()}
                    disabled={!canPreviousJobPage}
                    className="btn btn-sm btn-outline-primary"
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{jobPageIndex + 1}</strong> of{" "}
                    <strong>{jobPageOptions.length || 1}</strong>
                  </span>
                  <button
                    onClick={() => nextJobPage()}
                    disabled={!canNextJobPage}
                    className="btn btn-sm btn-outline-primary"
                  >
                    Next
                  </button>
                </div>
              </div>

              {editingJob && (
                <div
                  className="modal d-block"
                  tabIndex="-1"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Edit Job</h5>
                      </div>
                      <div className="modal-body">
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Role</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedJobData.role || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  role: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Location</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedJobData.location || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  location: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Department</label>
                            <select
                              className="form-select"
                              value={editedJobData.department || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  department: e.target.value,
                                })
                              }
                            >
                              <option value="">Select department</option>
                              {departmentOptions.map((dept) => (
                                <option
                                  key={dept.department_id}
                                  value={dept.department_name}
                                >
                                  {dept.department_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Education</label>
                            <select
                              className="form-select"
                              value={editedJobData.education || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  education: e.target.value,
                                })
                              }
                            >
                              <option value="">Select education</option>
                              {educationOptions.map((edu) => (
                                <option
                                  key={edu.education_id}
                                  value={edu.qualification}
                                >
                                  {edu.qualification}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Salary</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedJobData.salary || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  salary: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Experience</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedJobData.experience || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  experience: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Skills</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editedJobData.skills || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  skills: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Status</label>
                            <select
                              className="form-select"
                              value={editedJobData.status || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  status: e.target.value,
                                })
                              }
                            >
                              <option value="">Select Status</option>
                              <option value="Open">Open</option>
                              <option value="Closed">Closed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Apply Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={editedJobData.apply_by || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  apply_by: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Start Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={editedJobData.start_date || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  start_date: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">End Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={editedJobData.end_date || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  end_date: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Work Mode</label>
                            <select
                              className="form-select"
                              value={editedJobData.work_mode || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  work_mode: e.target.value,
                                })
                              }
                            >
                              <option value="">Select work mode</option>
                              <option value="Remote">Remote</option>
                              <option value="Hybrid">Hybrid</option>
                              <option value="Onsite">Onsite</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Employment Type
                            </label>
                            <select
                              className="form-select"
                              value={editedJobData.employment_type || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  employment_type: e.target.value,
                                })
                              }
                            >
                              <option value="" disabled>
                                Select Type
                              </option>
                              <option value="Full Time">Full Time</option>
                              <option value="Part Time">Part Time</option>
                              <option value="Contract">Contract</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Vacancies</label>
                            <input
                              type="number"
                              className="form-control"
                              value={editedJobData.vacancies || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  vacancies: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="form-label">
                              Good to Have Skills
                            </label>
                            <textarea
                              rows={2}
                              className="form-control"
                              value={editedJobData.good_to_have_skills || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  good_to_have_skills: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="form-label">Benefits</label>
                            <textarea
                              rows={2}
                              className="form-control"
                              value={editedJobData.benefits || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  benefits: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-md-12 mb-3">
                            <label className="form-label">
                              Job Description
                            </label>
                            <textarea
                              rows={3}
                              className="form-control"
                              value={editedJobData.job_description || ""}
                              onChange={(e) =>
                                setEditedJobData({
                                  ...editedJobData,
                                  job_description: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn btn-secondary"
                          onClick={() => setEditingJob(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleSaveJob}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {viewingContent && (
              <div
                className="modal fade show d-block"
                tabIndex="-1"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
                onClick={() => setViewingContent(null)}
              >
                <div
                  className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        {viewingContent.title || "Description"}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setViewingContent(null)}
                      />
                    </div>
                    <div className="modal-body">
                      <div className="job-content-full">
                        {viewingContent.content}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <h2 className="section-heading">Courses:</h2>
            <div className="card-custom">
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="Search course materials..."
                  value={CourseMaterialFilter}
                  onChange={(e) => setCourseMaterialFilter(e.target.value)}
                />
                <button
                  className="add-button"
                  onClick={() => navigate("/user-course-materials")}
                >
                  Add
                </button>
              </div>

              <div className="table-responsive">
                <table
                  {...getCourseTableProps()}
                  className="table table-bordered table-striped"
                >
                  <thead className="table-light">
                    {courseHeaderGroups.map((group) => (
                      <tr {...group.getHeaderGroupProps()} key={group.id}>
                        {group.headers.map((column) => (
                          <th
                            {...column.getHeaderProps(
                              column.getSortByToggleProps()
                            )}
                            key={column.id}
                          >
                            {column.render("Header")}
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " ▼"
                                : " ▲"
                              : ""}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getCourseTableBodyProps()}>
                    {coursePage.length === 0 ? (
                      <tr>
                        <td colSpan={courseColumns.length}>
                          No course materials found.
                        </td>
                      </tr>
                    ) : (
                      coursePage.map((row) => {
                        prepareCourseRow(row);
                        return (
                          <tr {...row.getRowProps()} key={row.id}>
                            {row.cells.map((cell) => (
                              <td {...cell.getCellProps()} key={cell.column.id}>
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                <div className="pagination d-flex align-items-center gap-2 mt-3">
                  <button
                    onClick={() => previousCoursePage()}
                    disabled={!canPreviousCoursePage}
                    className="btn btn-sm btn-outline-primary"
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{coursePageIndex + 1}</strong> of{" "}
                    <strong>{coursePageOptions.length}</strong>
                  </span>
                  <button
                    onClick={() => nextCoursePage()}
                    disabled={!canNextCoursePage}
                    className="btn btn-sm btn-outline-primary"
                  >
                    Next
                  </button>
                </div>
              </div>

              {editingCourseMaterial && (
                <div
                  className="modal d-block"
                  tabIndex="-1"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Edit Course Material</h5>
                      </div>
                      <div className="modal-body">
                        <div className="mb-3">
                          <label>Profile</label>
                          <select
                            className="form-select"
                            value={editedCourseMaterial.profile || ""}
                            onChange={(e) =>
                              setEditedCourseMaterial({
                                ...editedCourseMaterial,
                                profile: parseInt(e.target.value),
                              })
                            }
                          >
                            <option value="">Select Profile</option>
                            {profiles.map((profile) => (
                              <option
                                key={profile.profile_id}
                                value={profile.profile_id}
                              >
                                {profile.profile_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-3">
                          <label>Course</label>
                          <select
                            className="form-select"
                            value={editedCourseMaterial.course || ""}
                            onChange={(e) =>
                              setEditedCourseMaterial({
                                ...editedCourseMaterial,
                                course: parseInt(e.target.value),
                              })
                            }
                          >
                            <option value="">Select Course</option>
                            {courseOptions.map((courseOption) => (
                              <option
                                key={courseOption.course_id}
                                value={courseOption.course_id}
                              >
                                {courseOption.course_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mb-3">
                          <label>Sub Topics</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editedCourseMaterial.sub_topics || ""}
                            onChange={(e) =>
                              setEditedCourseMaterial({
                                ...editedCourseMaterial,
                                sub_topics: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="mb-3">
                          <label>Description</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={editedCourseMaterial.description || ""}
                            onChange={(e) =>
                              setEditedCourseMaterial({
                                ...editedCourseMaterial,
                                description: e.target.value,
                              })
                            }
                          ></textarea>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn btn-secondary"
                          onClick={() => setEditingCourseMaterial(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleSaveCourseMaterial}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
